import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { ReactElement } from 'react';

import { AdminPanel } from 'components/featured/AdminPanel/AdminPanel';
import AsanaProjectDetails from 'components/featured/Asana/AsanaProjectDetails/AsanaProjectDetails';
import ClientSelected from 'components/featured/Client/ClientSelected/ClientSelected';
import SocialInsightsPage from 'components/featured/Leads/SocialInsights';
import TeamSelected from 'components/featured/Team/TeamSelected';
import ZoomInfoSelected from 'components/featured/ZoomInfo/AdminSearch/ZoomInfoSelected';
import BusinessSelected from 'components/featured/ZoomInfo/BusinessSelected';

import MainLayout from 'layout/layout';

import ErrorComponent from 'pages/404/404';
import Asana from 'pages/Asana/Asana';
import Businesses from 'pages/Businesses';
import { SelectedRoleAndPermission } from 'pages/Dashboard/Admin/SelectedRoleAndPermission';
import Client from 'pages/Dashboard/Client/Client';
import ClientEdit from 'pages/Dashboard/Client/ClientEdit';
import ClientNew from 'pages/Dashboard/Client/ClientNew';
import Contacts from 'pages/Dashboard/Contacts';
import Dashboard from 'pages/Dashboard/Dashboard/Dashboard';
import Deals from 'pages/Dashboard/Deals';
import DealDetails from 'pages/Dashboard/Deals/DealDetails';
import DealEdit from 'pages/Dashboard/Deals/DealEdit';
import DealNew from 'pages/Dashboard/Deals/DealNew';
import Leads from 'pages/Dashboard/Leads';
import { Tasks } from 'pages/Dashboard/Tasks';
import Team from 'pages/Dashboard/Team/Team';
import ForgotPassword from 'pages/ForgotPassword/ForgotPassword';
import LoginScreen from 'pages/LoginScreen/LoginScreen';
import Reports from 'pages/Reports';
import ResetPassword from 'pages/ResetPassword/ResetPassword';
import Privacy from 'pages/Terms-Privacy/Privacy';
import Terms from 'pages/Terms-Privacy/Terms';

import CompleteAccount from 'pages/CompleteAccount';
import WelcomeAbroad from 'pages/WelcomeAboard';
import { usePermissions } from 'customHook/usePermissions';
import { loader, ProtectedRoute } from './ProtectedRoutes';
import RoutePaths from './RoutePaths';

const Root = () => {
  return <Navigate to={RoutePaths.Login} />;
};

const NotFoundRedirect = () => {
  return <Navigate to='/404' replace />;
};

type ActionPermissionRule = {
  module: string;
  subModule: string;
  action: string;
};

const PermissionRoute = ({
  children,
  module,
  subModule,
  action,
  anyOfActions,
  requireAdmin = false,
}: {
  children: ReactElement;
  module?: string;
  subModule?: string;
  action?: string;
  anyOfActions?: ActionPermissionRule[];
  requireAdmin?: boolean;
}) => {
  const { hasModuleAccess, hasAction, hasAtleastAdminAccess } = usePermissions();

  if (requireAdmin && !hasAtleastAdminAccess()) {
    return <NotFoundRedirect />;
  }

  if (action && module && subModule && !hasAction(module, subModule, action)) {
    return <NotFoundRedirect />;
  }

  if (anyOfActions?.length) {
    const canAccessFromAnyAction = anyOfActions.some(({ module, subModule, action }) =>
      hasAction(module, subModule, action),
    );

    if (!canAccessFromAnyAction) {
      return <NotFoundRedirect />;
    }
  }

  if (!action && !anyOfActions?.length && module && !hasModuleAccess(module, subModule)) {
    return <NotFoundRedirect />;
  }

  return children;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path='/' errorElement={<ErrorComponent />}>
      <Route index element={<Root />} />
      <Route path='/404' element={<ErrorComponent />} />
      <Route path={RoutePaths.Login} element={<LoginScreen />} />
      <Route path={RoutePaths.ResetPassword} element={<ResetPassword />} />
      <Route path={RoutePaths.ForgotPassword} element={<ForgotPassword />} />
      <Route path={RoutePaths.CompleteAccount} element={<CompleteAccount />} />
      <Route path={RoutePaths.WelcomeAboard} element={<WelcomeAbroad />} />
      <Route path={RoutePaths.Terms} element={<Terms />} />
      <Route path={RoutePaths.Privacy} element={<Privacy />} />
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
        loader={loader}
      >
        <Route
          path={RoutePaths.Home}
          element={
            <PermissionRoute module='Dashboard'>
              <Dashboard />
            </PermissionRoute>
          }
        />
        <Route
          path={`${RoutePaths.Home}/admin`}
          element={
            <PermissionRoute requireAdmin>
              <AdminPanel />
            </PermissionRoute>
          }
        />
        <Route
          path={`${RoutePaths.Home}/admin/role/:id`}
          element={
            <PermissionRoute requireAdmin>
              <SelectedRoleAndPermission />
            </PermissionRoute>
          }
        />

        <Route
          path={`${RoutePaths.Leads}`}
          element={
            <PermissionRoute module='Leads'>
              <Leads />
            </PermissionRoute>
          }
        />

        <Route
          path={RoutePaths.Client}
          element={
            <PermissionRoute module='Clients'>
              <Client />
            </PermissionRoute>
          }
        />
        <Route
          path={`${RoutePaths.Client}/new`}
          element={
            <PermissionRoute module='Clients' subModule='clientlist' action='create'>
              <ClientNew />
            </PermissionRoute>
          }
        />
        <Route
          path={`${RoutePaths.Client}/:id/edit`}
          element={
            <PermissionRoute module='Clients' subModule='clientlist' action='edit'>
              <ClientEdit />
            </PermissionRoute>
          }
        />
        <Route
          path={`${RoutePaths.Client}/huddle/:id`}
          element={
            <PermissionRoute module='Clients' subModule='clientlist' action='view'>
              <ClientSelected />
            </PermissionRoute>
          }
        />
        <Route
          path={`${RoutePaths.Client}/:id`}
          element={
            <PermissionRoute module='Clients' subModule='clientlist' action='view'>
              <ClientSelected />
            </PermissionRoute>
          }
        />

        <Route
          path={RoutePaths.Team}
          element={
            <PermissionRoute module='Teams'>
              <Team />
            </PermissionRoute>
          }
        />
        <Route
          path={`${RoutePaths.Team}/:id`}
          element={
            <PermissionRoute module='Teams'>
              <TeamSelected />
            </PermissionRoute>
          }
        />

        <Route
          path={RoutePaths.Tasks}
          element={
            <PermissionRoute module='Tasks'>
              <Tasks />
            </PermissionRoute>
          }
        />

        <Route path={RoutePaths.Deals}>
          <Route
            index
            element={
              <PermissionRoute module='Deals'>
                <Deals />
              </PermissionRoute>
            }
          />
          <Route
            path={`new`}
            element={
              <PermissionRoute
                anyOfActions={[
                  { module: 'Deals', subModule: 'MyDeals', action: 'create' },
                  { module: 'Deals', subModule: 'AllDeals', action: 'create' },
                ]}
              >
                <DealNew />
              </PermissionRoute>
            }
          />
          <Route
            path={`:id/edit`}
            element={
              <PermissionRoute
                anyOfActions={[
                  { module: 'Deals', subModule: 'MyDeals', action: 'edit' },
                  { module: 'Deals', subModule: 'AllDeals', action: 'edit' },
                ]}
              >
                <DealEdit />
              </PermissionRoute>
            }
          />
          <Route
            path={`:id`}
            element={
              <PermissionRoute module='Deals' subModule='DealDetails' action='view'>
                <DealDetails />
              </PermissionRoute>
            }
          />
        </Route>

        <Route
          path={RoutePaths.Asana}
          element={
            <PermissionRoute module='Asana'>
              <Asana />
            </PermissionRoute>
          }
        />
        <Route
          path={`${RoutePaths.Asana}/:id`}
          element={
            <PermissionRoute module='Asana'>
              <AsanaProjectDetails />
            </PermissionRoute>
          }
        />

        <Route path={RoutePaths.Businesses}>
          <Route
            index
            element={
              <PermissionRoute module='Businesses'>
                <Businesses />
              </PermissionRoute>
            }
          />
          <Route
            path={':id'}
            element={
              <PermissionRoute
                module='Businesses'
                subModule='BusinessDetailsRelationship'
                action='view'
              >
                <BusinessSelected />
              </PermissionRoute>
            }
          />
        </Route>

        <Route
          path={`${RoutePaths.ZoomInfo}/:id`}
          element={
            <PermissionRoute module='Businesses'>
              <ZoomInfoSelected />
            </PermissionRoute>
          }
        />

        <Route
          path={RoutePaths.Reports}
          element={
            <PermissionRoute module='Reports'>
              <Reports />
            </PermissionRoute>
          }
        />

        <Route
          path='/leads/new-lead/social-insights'
          element={
            <PermissionRoute module='Leads'>
              <SocialInsightsPage />
            </PermissionRoute>
          }
        />
        <Route
          path={RoutePaths.Contacts}
          element={
            <PermissionRoute module='Contacts'>
              <Contacts />
            </PermissionRoute>
          }
        />
      </Route>
    </Route>,
  ),
);

const Routes = () => {
  return <RouterProvider router={router} />;
};

export default Routes;
